import { inject } from '@angular/core';
import { signalStore, withState, withMethods, patchState } from '@ngrx/signals';
import { Observable } from 'rxjs';
import { NormtiderService } from './normtider.service';
import { Normtider } from '../../../timetrack/store/models';
import { AlertService } from '../../../core/alerts/alert.service';

type EntryUpdate = { mid: number; date: Date; timer: number; kode?: number };
type NormtiderPayload = Omit<Partial<Normtider>, 'dato'> & { dato: any };

function toBackendDateString(date: Date | string): string {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');

    return `${year}-${month}-${day} 00:00:00.000`;
}

export const NormtiderStore = signalStore(
    { providedIn: 'root' },
    withState({ entries: [] as Normtider[], loading: false, loadError: false, saving: false }),
    withMethods((store, service = inject(NormtiderService), alerts = inject(AlertService)) => {
        let requestVersion = 0;
        let currentMid: number;
        let currentYear: number;
        const inContext = (entry: Normtider) => entry.mid === currentMid && new Date(entry.dato).getFullYear() === currentYear;
        const merge = (updates: Normtider[]) => {
            const entries = new Map(store.entries().map(e => [e.id, e]));
            updates.filter(inContext).forEach(e => entries.set(e.id, e));
            patchState(store, { entries: Array.from(entries.values()) });
        };
        async function save<T>(request: Observable<T>, apply: (result: T) => void): Promise<boolean> {
            if (store.saving()) { return false; }
            const version = requestVersion;
            patchState(store, { saving: true });
            try {
                const result = await request.toPromise();
                if (version === requestVersion) { apply(result); }
                return true;
            } catch {
                alerts.createAlert('Ændringen kunne ikke gemmes. Prøv igen.', 'danger');
                return false;
            } finally {
                patchState(store, { saving: false });
            }
        }
        return {
            loadForUserYear(mid: number, year: number) {
                const version = ++requestVersion;
                currentMid = mid;
                currentYear = year;
                patchState(store, { entries: [], loading: true, loadError: false });
                service.getByUserAndYear(mid, year).subscribe({
                    next: entries => {
                        if (version === requestVersion) { patchState(store, { entries: entries.filter(inContext), loading: false }); }
                    },
                    error: () => {
                        if (version === requestVersion) { patchState(store, { loading: false, loadError: true }); }
                    }
                });
            },
            upsertEntry(entry: EntryUpdate) {
                const existing = store.entries().find(e => e.mid === entry.mid && toBackendDateString(e.dato) === toBackendDateString(entry.date));
                const kode = entry.kode ?? existing?.kode ?? 0;
                if (entry.timer == null) {
                    return existing ? this.deleteEntry(existing.id) : Promise.resolve(true);
                }
                const request = existing
                    ? service.update(existing.id, entry.timer, kode)
                    : service.create({ mid: entry.mid, dato: toBackendDateString(entry.date), timer: entry.timer, kode, kontor: 0 } as NormtiderPayload);
                return save(request, result => merge([result]));
            },
            deleteEntry(id: number) {
                return save(service.delete(id), () => patchState(store, { entries: store.entries().filter(e => e.id !== id) }));
            },
            bulkUpsertMultipleUser(entries: { mid: number; dato: string | Date; timer: number; kode: number; kontor: number }[]) {
                const payload = entries.map(e => ({ ...e, dato: toBackendDateString(e.dato) } as NormtiderPayload));
                return save(service.bulkUpsert(payload), merge);
            },
            bulkUpsert(entries: EntryUpdate[]) {
                const payload = entries.map(e => {
                    const existing = store.entries().find(old => old.mid === e.mid && toBackendDateString(old.dato) === toBackendDateString(e.date));
                    return {
                        mid: e.mid, dato: toBackendDateString(e.date), timer: e.timer ?? 0,
                        kode: e.kode ?? existing?.kode ?? 0, kontor: existing?.kontor ?? 0
                    } as NormtiderPayload;
                });
                return save(service.bulkUpsert(payload), merge);
            }
        };
    })
);
